import api, { unwrap } from './client';
import type { ApiResponse, PageResponse, SearchParams } from '@/types/api';
import type {
  SocialAccountRequest,
  SocialAccountResponse,
  SocialAnalyticsDashboard,
  SocialCalendarEntry,
  SocialCampaign,
  SocialCampaignRequest,
  SocialComment,
  SocialCompetitor,
  SocialCompetitorRequest,
  SocialConversationResponse,
  SocialDashboardSummary,
  SocialListeningKeyword,
  SocialListeningKeywordRequest,
  SocialListeningMatch,
  SocialMessageResponse,
  SocialMonitoringSummary,
  SocialPostRequest,
  SocialPostResponse,
  SocialReview,
  SocialBrand,
  SocialBrandRequest,
  SocialIntegrationConfig,
  SocialIntegrationConfigRequest,
  SocialConnectionOverview,
  SocialChannelHealth,
  SocialConnectionTestResult,
  SocialConnectionLog,
  SocialAutomationRule,
  SocialAutomationRuleRequest,
  SocialMediaFolder,
  SocialMediaAsset,
  SocialReviewsSummary,
} from '@/types/social';

export const socialApi = {
  dashboardSummary: () =>
    unwrap(api.get<ApiResponse<SocialDashboardSummary>>('/v1/social/dashboard/summary')),

  monitoringSummary: () =>
    unwrap(api.get<ApiResponse<SocialMonitoringSummary>>('/v1/social/analytics/monitoring')),

  analyticsDashboard: () =>
    unwrap(api.get<ApiResponse<SocialAnalyticsDashboard>>('/v1/social/analytics/dashboard')),

  listAccounts: () =>
    unwrap(api.get<ApiResponse<SocialAccountResponse[]>>('/v1/social/accounts')),

  disconnectAccount: (id: number) =>
    unwrap(api.delete<ApiResponse<null>>(`/v1/social/accounts/${id}`)),

  getOAuthUrl: (accountId: number) =>
    unwrap(api.get<ApiResponse<{ url: string }>>(`/v1/social/accounts/${accountId}/oauth/url`)),

  demoConnect: (accountId: number) =>
    unwrap(api.post<ApiResponse<SocialAccountResponse>>(`/v1/social/accounts/${accountId}/oauth/demo-connect`)),

  connectAccount: (body: SocialAccountRequest) =>
    unwrap(api.post<ApiResponse<SocialAccountResponse>>('/v1/social/accounts', body)),

  reconnectAccount: (id: number) =>
    unwrap(api.post<ApiResponse<SocialAccountResponse>>(`/v1/social/accounts/${id}/reconnect`)),

  markConversationRead: (conversationId: number) =>
    unwrap(api.post<ApiResponse<null>>(`/v1/social/inbox/conversations/${conversationId}/read`)),

  simulateInbound: (socialAccountId: number, message: string) =>
    unwrap(
      api.post<ApiResponse<SocialConversationResponse>>('/v1/social/inbox/simulate-inbound', {
        socialAccountId,
        message,
        conversationId: `demo_${Date.now()}`,
      }),
    ),

  listConversations: (params?: SearchParams) =>
    unwrap(api.get<ApiResponse<PageResponse<SocialConversationResponse>>>('/v1/social/inbox/conversations', { params })),

  listMessages: (conversationId: number, params?: SearchParams) =>
    unwrap(
      api.get<ApiResponse<PageResponse<SocialMessageResponse>>>(
        `/v1/social/inbox/conversations/${conversationId}/messages`,
        { params },
      ),
    ),

  sendMessage: (conversationId: number, body: { messageContent: string }) =>
    unwrap(
      api.post<ApiResponse<SocialMessageResponse>>(
        `/v1/social/inbox/conversations/${conversationId}/messages`,
        body,
      ),
    ),

  listPosts: (params?: SearchParams) =>
    unwrap(api.get<ApiResponse<PageResponse<SocialPostResponse>>>('/v1/social/posts', { params })),

  createPost: (body: SocialPostRequest) =>
    unwrap(api.post<ApiResponse<SocialPostResponse>>('/v1/social/posts', body)),

  publishPost: (id: number) =>
    unwrap(api.post<ApiResponse<SocialPostResponse>>(`/v1/social/posts/${id}/publish`)),

  approvePost: (id: number) =>
    unwrap(api.post<ApiResponse<SocialPostResponse>>(`/v1/social/posts/${id}/approve`)),

  uploadPostMedia: (postId: number, file: File) => {
    const form = new FormData();
    form.append('file', file);
    return unwrap(api.post<ApiResponse<{ fileUrl: string }>>(`/v1/social/posts/${postId}/media`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }));
  },

  syncAnalytics: () => unwrap(api.post<ApiResponse<null>>('/v1/social/analytics/sync')),

  exportPostsCsv: () =>
    api.get('/v1/social/reports/posts/export.csv', { responseType: 'blob' }),

  exportInboxCsv: () =>
    api.get('/v1/social/reports/inbox/export.csv', { responseType: 'blob' }),

  listCampaigns: (params?: SearchParams) =>
    unwrap(api.get<ApiResponse<PageResponse<SocialCampaign>>>('/v1/social/campaigns', { params })),

  createCampaign: (body: SocialCampaignRequest) =>
    unwrap(api.post<ApiResponse<SocialCampaign>>('/v1/social/campaigns', body)),

  submitCampaignApproval: (id: number) =>
    unwrap(api.post<ApiResponse<SocialCampaign>>(`/v1/social/campaigns/${id}/submit-approval`)),

  listReviews: (params?: SearchParams) =>
    unwrap(api.get<ApiResponse<PageResponse<SocialReview>>>('/v1/social/reviews', { params })),

  listComments: (params?: SearchParams) =>
    unwrap(api.get<ApiResponse<PageResponse<SocialComment>>>('/v1/social/reviews/comments', { params })),

  replyComment: (id: number, replyText: string) =>
    unwrap(
      api.post<ApiResponse<SocialComment>>(`/v1/social/reviews/comments/${id}/reply`, { replyText }),
    ),

  replyReview: (id: number, replyText: string) =>
    unwrap(api.post<ApiResponse<SocialReview>>(`/v1/social/reviews/${id}/reply`, { replyText })),

  listListeningKeywords: () =>
    unwrap(api.get<ApiResponse<SocialListeningKeyword[]>>('/v1/social/listening/keywords')),

  addListeningKeyword: (body: SocialListeningKeywordRequest) =>
    unwrap(api.post<ApiResponse<SocialListeningKeyword>>('/v1/social/listening/keywords', body)),

  listListeningMatches: (params?: SearchParams) =>
    unwrap(api.get<ApiResponse<PageResponse<SocialListeningMatch>>>('/v1/social/listening/matches', { params })),

  listCompetitors: () =>
    unwrap(api.get<ApiResponse<SocialCompetitor[]>>('/v1/social/listening/competitors')),

  addCompetitor: (body: SocialCompetitorRequest) =>
    unwrap(api.post<ApiResponse<SocialCompetitor>>('/v1/social/listening/competitors', body)),

  listBrands: () =>
    unwrap(api.get<ApiResponse<SocialBrand[]>>('/v1/social/setup/brands')),

  createBrand: (body: SocialBrandRequest) =>
    unwrap(api.post<ApiResponse<SocialBrand>>('/v1/social/setup/brands', body)),

  listCalendar: (from: string, to: string, page = 0, size = 100) =>
    unwrap(
      api.get<ApiResponse<PageResponse<SocialCalendarEntry>>>('/v1/social/setup/calendar', {
        params: { from, to, page, size },
      }),
    ),

  reschedulePost: (postId: number, scheduledAt: string) =>
    unwrap(
      api.post<ApiResponse<SocialCalendarEntry>>(`/v1/social/setup/calendar/${postId}/reschedule`, {
        scheduledAt,
      }),
    ),

  assignConversation: (id: number, employeeId: number) =>
    unwrap(
      api.post<ApiResponse<null>>(`/v1/social/setup/inbox/conversations/${id}/assign`, { employeeId }),
    ),

  escalateConversation: (id: number) =>
    unwrap(api.post<ApiResponse<null>>(`/v1/social/setup/inbox/conversations/${id}/escalate`)),

  listIntegrationConfigs: () =>
    unwrap(api.get<ApiResponse<SocialIntegrationConfig[]>>('/v1/social/setup/integration-config')),

  saveIntegrationConfig: (body: SocialIntegrationConfigRequest) =>
    unwrap(api.post<ApiResponse<SocialIntegrationConfig>>('/v1/social/setup/integration-config', body)),

  simulateReview: (socialAccountId: number, reviewText: string, rating = 4) =>
    unwrap(
      api.post<ApiResponse<SocialReview>>('/v1/social/reviews/simulate', {
        socialAccountId,
        reviewText,
        rating,
      }),
    ),

  listPendingApprovals: () =>
    unwrap(
      api.get<
        ApiResponse<
          { id: number; ownerModule: string; entityType: string; entityId: number; approvalStatus: string }[]
        >
      >('/v1/shared/approvals/pending', { params: { ownerModule: 'SOCIAL' } }),
    ),

  approveApprovalRequest: (id: number) =>
    unwrap(api.post<ApiResponse<{ status: string }>>(`/v1/shared/approvals/${id}/approve`)),

  rejectApprovalRequest: (id: number, notes?: string) =>
    unwrap(
      api.post<ApiResponse<{ status: string }>>(`/v1/shared/approvals/${id}/reject`, null, {
        params: notes ? { notes } : undefined,
      }),
    ),

  connectionOverview: () =>
    unwrap(api.get<ApiResponse<SocialConnectionOverview>>('/v1/social/connections/overview')),

  channelHealth: (accountId: number) =>
    unwrap(api.get<ApiResponse<SocialChannelHealth>>(`/v1/social/connections/health/${accountId}`)),

  refreshChannelToken: (accountId: number) =>
    unwrap(
      api.post<ApiResponse<SocialChannelHealth>>(`/v1/social/connections/accounts/${accountId}/refresh-token`),
    ),

  runConnectionTests: (accountId: number, type?: string) =>
    unwrap(
      api.post<ApiResponse<SocialConnectionTestResult[]>>(
        `/v1/social/connections/accounts/${accountId}/test`,
        null,
        { params: type ? { type } : undefined },
      ),
    ),

  connectionLogs: (params?: SearchParams) =>
    unwrap(api.get<ApiResponse<PageResponse<SocialConnectionLog>>>('/v1/social/connections/logs', { params })),

  listAutomationRules: () =>
    unwrap(api.get<ApiResponse<SocialAutomationRule[]>>('/v1/social/automation/rules')),

  createAutomationRule: (body: SocialAutomationRuleRequest) =>
    unwrap(api.post<ApiResponse<SocialAutomationRule>>('/v1/social/automation/rules', body)),

  deleteAutomationRule: (id: number) =>
    unwrap(api.delete<ApiResponse<null>>(`/v1/social/automation/rules/${id}`)),

  toggleAutomationRule: (id: number, active: boolean) =>
    unwrap(api.post<ApiResponse<SocialAutomationRule>>(`/v1/social/automation/rules/${id}/toggle`, { active })),

  listMediaFolders: () =>
    unwrap(api.get<ApiResponse<SocialMediaFolder[]>>('/v1/social/media/folders')),

  createMediaFolder: (name: string, parentFolderId?: number) =>
    unwrap(
      api.post<ApiResponse<SocialMediaFolder>>('/v1/social/media/folders', { name, parentFolderId }),
    ),

  listMediaAssets: (params?: SearchParams & { folderId?: number }) =>
    unwrap(api.get<ApiResponse<PageResponse<SocialMediaAsset>>>('/v1/social/media/assets', { params })),

  uploadMediaAsset: (file: File, folderId?: number, tags?: string) => {
    const form = new FormData();
    form.append('file', file);
    return unwrap(
      api.post<ApiResponse<SocialMediaAsset>>('/v1/social/media/assets', form, {
        params: { folderId, tags },
        headers: { 'Content-Type': 'multipart/form-data' },
      }),
    );
  },

  deleteMediaAsset: (id: number) =>
    unwrap(api.delete<ApiResponse<null>>(`/v1/social/media/assets/${id}`)),

  reviewsSummary: () =>
    unwrap(api.get<ApiResponse<SocialReviewsSummary>>('/v1/social/reviews/summary')),

  assignComment: (id: number, employeeId: number) =>
    unwrap(api.post<ApiResponse<SocialComment>>(`/v1/social/reviews/comments/${id}/assign`, { employeeId })),

  resolveComment: (id: number) =>
    unwrap(api.post<ApiResponse<SocialComment>>(`/v1/social/reviews/comments/${id}/resolve`)),

  listInternalNotes: (entityType: string, entityId: number) =>
    unwrap(
      api.get<ApiResponse<{ id: number; entityType: string; entityId: number; noteText: string; createdAt?: string }[]>>(
        '/v1/social/setup/notes',
        { params: { entityType, entityId } },
      ),
    ),

  addInternalNote: (entityType: string, entityId: number, noteText: string) =>
    unwrap(
      api.post<ApiResponse<{ id: number; noteText: string; createdAt?: string }>>('/v1/social/setup/notes', {
        entityType,
        entityId,
        noteText,
      }),
    ),

  listTags: () =>
    unwrap(api.get<ApiResponse<{ id: number; name: string; color?: string }[]>>('/v1/social/setup/tags')),

  createTag: (name: string, color?: string) =>
    unwrap(api.post<ApiResponse<{ id: number; name: string }>>('/v1/social/setup/tags', { name, color })),
};
